import {
  bandForCap,
  capLabel,
  membersNeededToAdvance,
  nextBandForCap,
} from 'common/tiers';
import React from 'react';

// A limit hand-set between tiers is quoted as-is -- a 250 really can connect
// 250 -- but advancing needs half the *tier cap*, since that is what the
// request form measures against.
const tierSummary = (
  limit: number | null | undefined,
  connected: number,
): React.ReactNode => {
  const band = bandForCap(limit);
  if (!band) {
    return <>Open a request for higher limits or a support question.</>;
  }
  const connectedLabel = connected.toLocaleString();
  if (band.cap === null) {
    return (
      <>
        Your app is on <b>{band.name}</b> and has no member limit. You have{' '}
        <b>{connectedLabel}</b> connected members. Open a request if you need
        higher API rate limits or have a support question.
      </>
    );
  }

  const onTier = (
    <>
      Your app is on <b>{band.name}</b>, with a limit of{' '}
      {(limit ?? band.cap).toLocaleString()} connected members.
    </>
  );

  const nextBand = nextBandForCap(limit);
  if (!nextBand) {
    return onTier;
  }
  const nextTier = (
    <>
      <b>{nextBand.name}</b> ({capLabel(nextBand.cap)} members)
    </>
  );
  // The 50% gate starts at Build, so Sandbox has nothing to earn yet. Only Sandbox
  // reaches that case here: an unknown cap and an unlimited one returned above.
  const requiredToAdvance = membersNeededToAdvance(limit);
  const gateApplies = requiredToAdvance !== undefined;
  if (!gateApplies) {
    return (
      <>
        {onTier} You can apply for {nextTier} whenever you are ready. From{' '}
        {nextBand.name} onward, moving up needs half your tier cap connected.
      </>
    );
  }

  if (connected >= requiredToAdvance) {
    return (
      <>
        {onTier} You have <b>{connectedLabel}</b> connected, so you can apply
        for {nextTier} now.
      </>
    );
  }

  return (
    <>
      {onTier} To apply for {nextTier} you need{' '}
      <b>{requiredToAdvance.toLocaleString()} connected members</b> &mdash; half
      the {band.name} cap. You have <b>{connectedLabel}</b>. You can still
      request higher API rate limits at any time.
    </>
  );
};

export default tierSummary;
